/* Global Liebreiz campaign-attribute cleanup; no tracking. */
(function(scope){
const module={exports:{}};
'use strict';

// Only Liebreiz's optional campaign attributes. No customer, item or service data.
const OWNED_ATTRIBUTES = Object.freeze([
  '__lws_entry_lp', '__lws_last_lp', '__lws_utm_source', '__lws_utm_medium',
  '__lws_utm_campaign', '__lws_utm_content', '__lws_utm_term'
]);
const STORAGE_KEYS = Object.freeze(['liebreiz.cartAttribution.v1', 'liebreiz.entryLP.v1']);

// A theme-only entry point for native product/cart pages which have no SPA
// footer. Never bind, dispatch or replay clicks on an existing SPA link.
function installPrivacyPreferencesEntry(scope) {
  const document = scope?.document;
  if (!document?.querySelector || !document.createElement || !document.addEventListener) return null;
  const selector = 'a[href="#shopifyReshowConsentBanner"]';
  let container = null, button = null, status = null, opening = false, observer = null;
  function refresh() {
    const existing = document.querySelector(selector);
    if (existing) {
      const transferFocus = document.activeElement === button;
      container?.remove();
      if (transferFocus) existing.focus?.();
      return;
    }
    if (!document.body) return;
    if (!container) {
      container = document.createElement('div'); container.id = 'lws-cookie-preferences-entry';
      container.setAttribute('role', 'group'); container.setAttribute('aria-label', 'Datenschutzeinstellungen');
      button = document.createElement('button'); button.type = 'button';
      button.textContent = 'Cookie-Einstellungen'; button.setAttribute('aria-describedby', 'lws-cookie-preferences-status');
      status = document.createElement('p'); status.id = 'lws-cookie-preferences-status';
      status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite');
      button.addEventListener('click', async () => {
        if (opening) return;
        opening = true; button.disabled = true; status.textContent = '';
        try {
          if (typeof scope.privacyBanner?.showPreferences !== 'function') throw new Error('preferences-unavailable');
          await scope.privacyBanner.showPreferences();
        } catch {
          status.textContent = 'Die Cookie-Einstellungen konnten gerade nicht geöffnet werden. Bitte lade die Seite neu und versuche es erneut. Deine Auswahl wurde durch diesen Aufruf nicht geändert.';
        } finally { opening = false; button.disabled = false; }
      });
      container.appendChild(button); container.appendChild(status);
    }
    if (!container.isConnected) document.body.appendChild(container);
  }
  function start() {
    refresh();
    if (!observer && document.body && typeof scope.MutationObserver === 'function') {
      observer = new scope.MutationObserver(refresh);
      observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });
    }
  }
  start();
  if (!document.body) document.addEventListener('DOMContentLoaded', start, { once: true });
  scope.addEventListener?.('pageshow', refresh);
  return Object.freeze({ refresh });
}

function installGlobalCartConsentGuard({ scope, autoStart = true, timeoutMs = 6000 } = {}) {
  if (!scope?.document?.addEventListener) return null;
  if (scope.LWSGlobalCartConsentGuard) return scope.LWSGlobalCartConsentGuard;
  const document = scope.document;
  let queue = Promise.resolve(), pending = 0, lastResult = null, errorCode = null;
  let eventDenied = false, consentGeneration = 0, activeNative = false;
  const bound = new WeakSet(), bypass = new WeakMap();

  function allowed() {
    try {
      const privacy = scope.Shopify?.customerPrivacy;
      return !eventDenied && privacy?.currentVisitorConsent?.().marketing === 'yes'
        && privacy?.marketingAllowed?.() === true;
    } catch { return false; }
  }
  function clearMemory() {
    for (const key of STORAGE_KEYS) {
      try { scope.sessionStorage?.removeItem(key); } catch { /* Storage denial never becomes consent. */ }
    }
  }
  function failure(reason) {
    const error = new Error('Deine Datenschutzeinstellung konnte noch nicht sicher übernommen werden. Bitte versuche „Zur Kasse“ erneut. Es wurde keine Bestellung ausgelöst.');
    error.code = 'LWS_CONSENT_CLEAR_REQUIRED'; error.reason = reason;
    return error;
  }
  function cartRoot() {
    const value = scope.Shopify?.routes?.root || '/';
    if (typeof value !== 'string' || !/^\/(?!\/)[a-zA-Z0-9_/-]*\/$|^\/$/.test(value)) throw failure('invalid-cart-root');
    return value;
  }
  async function clearServerNow() {
    clearMemory();
    if (typeof scope.fetch !== 'function' || typeof scope.AbortController !== 'function') throw failure('network-unavailable');
    const attributes = Object.fromEntries(OWNED_ATTRIBUTES.map(key => [key, '']));
    const controller = new scope.AbortController();
    let timer;
    try {
      // Always explicitly clear all seven keys: private __ attributes can be hidden
      // by Shopify's Ajax response, so absence in cart.js is NOT deletion proof.
      const request = Promise.resolve().then(() => scope.fetch(cartRoot() + 'cart/update.js', {
        method: 'POST', credentials: 'same-origin', cache: 'no-store',
        signal: controller.signal, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes })
      }));
      const timeout = new Promise((_, reject) => {
        timer = scope.setTimeout(() => { controller.abort(); reject(failure('timeout')); }, timeoutMs);
      });
      const response = await Promise.race([request, timeout]);
      if (!response?.ok) throw failure('http-failure');
      const cart = await Promise.race([response.json(), timeout]);
      if (!cart || !Array.isArray(cart.items) || !cart.attributes || typeof cart.attributes !== 'object' || Array.isArray(cart.attributes)) throw failure('invalid-cart-response');
      if (OWNED_ATTRIBUTES.some(key => cart.attributes[key] !== undefined && cart.attributes[key] !== null && cart.attributes[key] !== '')) throw failure('visible-attribution-remains');
      lastResult = { cleanupRequired: true, clearAcknowledged: true, privateReadbackVerified: false, attributesSent: OWNED_ATTRIBUTES.length };
      errorCode = null;
      return { ...lastResult };
    } catch (error) {
      errorCode = 'LWS_CONSENT_CLEAR_REQUIRED';
      if (error?.code === errorCode) throw error;
      throw failure('network-or-json-failure');
    } finally { scope.clearTimeout?.(timer); }
  }
  function serial(task) {
    pending++;
    const job = queue.catch(() => {}).then(task);
    queue = job.finally(() => { pending--; });
    // Prevent a background caller's intentionally handled failure from becoming
    // an unhandled rejection on the serialization tail.
    queue.catch(() => {});
    return job;
  }
  function cleanIfDenied() {
    if (!allowed()) clearMemory();
    return serial(() => allowed() ? { cleanupRequired: false } : clearServerNow());
  }
  function beforeCheckout() { return cleanIfDenied(); }

  // Call immediately after LWSAttributionV2 is assigned and BEFORE its first use.
  // This shares the queue with consented writes, preventing a previously started
  // Liebreiz POST from landing after revocation cleanup. No fetch monkey patch.
  function bindAttribution(api = scope.LWSAttributionV2) {
    if (!api || bound.has(api)) return false;
    const originalCapture = api.capture?.bind(api);
    const originalSync = api.sync?.bind(api);
    const originalCheckout = api.beforeCheckout?.bind(api);
    if (!originalSync || !originalCheckout || !originalCapture) return false;
    api.allowed = allowed;
    api.capture = function () { if (!allowed()) { clearMemory(); return null; } return originalCapture(); };
    const delegate = original => serial(async () => {
      if (!allowed()) return clearServerNow();
      let result;
      try { result = await original(); }
      catch (error) { if (!allowed()) return clearServerNow(); throw error; }
      return allowed() ? result : clearServerNow();
    });
    api.sync = () => delegate(originalSync);
    api.beforeCheckout = () => delegate(originalCheckout);
    bound.add(api);
    return true;
  }
  function observe(event) {
    const value = event?.detail?.marketingAllowed;
    if (value === false) eventDenied = true;
    else if (value === true) eventDenied = false;
    consentGeneration++;
    if (!allowed()) cleanIfDenied().catch(() => {});
  }
  function ownedFormInput(input) {
    const match = /^attributes\[([^\]]+)\]$/.exec(input?.name || '');
    return match && OWNED_ATTRIBUTES.includes(match[1]);
  }
  function scrubOwnedFormInputs(form) {
    // Prevent an old, optional hidden field from reintroducing the seven values.
    for (const input of Array.from(form?.elements || [])) if (ownedFormInput(input)) input.value = '';
  }
  function pathIs(value, suffix) {
    try {
      const url = new URL(value, scope.location.href);
      return url.origin === scope.location.origin && url.pathname.replace(/\/$/, '').endsWith(suffix);
    } catch { return false; }
  }
  function checkoutIntent(event) {
    const target = event?.target?.nodeType === 3 ? event.target.parentElement : event?.target;
    if (event.type === 'submit') {
      const form = target;
      const submitter = event.submitter;
      const action = submitter?.getAttribute?.('formaction') || form?.getAttribute?.('action') || '';
      if (pathIs(action, '/checkout') || (pathIs(action, '/cart') && (submitter?.name === 'checkout' || form?.querySelector?.('input[name="checkout"][type="hidden"]')))) return { type: 'form', node: form, submitter };
      return null;
    }
    const anchor = target?.closest?.('a[href]');
    if (!anchor || !pathIs(anchor.href, '/checkout')) return null;
    // Custom Ringfinder checks its own SKU/price/test-gateway invariants first.
    // Never replay it here: its bound beforeCheckout performs our final cleanup.
    if (anchor.hasAttribute?.('data-lr-checkout') && bound.has(scope.LWSAttributionV2)) return null;
    return { type: 'link', node: anchor };
  }
  function showError() {
    let message = document.getElementById?.('lws-consent-checkout-error');
    if (!message && document.body?.appendChild && document.createElement) {
      message = document.createElement('p'); message.id = 'lws-consent-checkout-error';
      message.setAttribute('role', 'alert'); message.setAttribute('tabindex', '-1');
      document.body.appendChild(message);
    }
    if (message) { message.textContent = failure().message; message.hidden = false; message.focus?.(); }
  }
  function hideError() { const message = document.getElementById?.('lws-consent-checkout-error'); if (message) message.hidden = true; }
  function intercept(event) {
    const intent = checkoutIntent(event);
    if (!intent) return;
    const permitted = bypass.get(intent.node);
    bypass.delete(intent.node);
    if (permitted && permitted.generation === consentGeneration && permitted.allowed === allowed()) return;
    if (allowed()) return;
    event.preventDefault(); event.stopImmediatePropagation?.();
    if (activeNative) return;
    activeNative = true;
    beforeCheckout().then(() => {
      if (intent.type === 'form' && !allowed()) scrubOwnedFormInputs(intent.node);
      hideError();
      // Replay the SAME native action, preserving form validation and other
      // theme listeners. Never directly assign location or call form.submit().
      bypass.set(intent.node, { generation: consentGeneration, allowed: allowed() });
      try {
        if (intent.type === 'form') {
          if (typeof intent.node.requestSubmit !== 'function') throw failure('native-submit-unavailable');
          intent.node.requestSubmit(intent.submitter || undefined);
        } else intent.node.click();
      } finally {
        // Failed native validation may suppress the replayed submit event. A
        // one-shot permit must never survive for a later, unrelated user click.
        bypass.delete(intent.node);
      }
    }).catch(() => { bypass.delete(intent.node); showError(); }).finally(() => { activeNative = false; });
  }
  const control = Object.freeze({
    allowed, beforeCheckout, cleanIfDenied, bindAttribution, observe, checkoutIntent,
    status: () => ({ pending, errorCode, consentGeneration, explicitMarketingAllowed: allowed(), lastResult: lastResult && { ...lastResult } })
  });
  scope.LWSGlobalCartConsentGuard = control;
  installPrivacyPreferencesEntry(scope);
  document.addEventListener('visitorConsentCollected', observe, true);
  document.addEventListener('click', intercept, true);
  document.addEventListener('submit', intercept, true);
  scope.addEventListener?.('pageshow', () => { if (!allowed()) cleanIfDenied().catch(() => {}); });
  bindAttribution();
  if (autoStart && !allowed()) cleanIfDenied().catch(() => {});
  return control;
}
module.exports = { OWNED_ATTRIBUTES, STORAGE_KEYS, installGlobalCartConsentGuard, installPrivacyPreferencesEntry };

if(scope?.document)module.exports.installGlobalCartConsentGuard({scope});
})(typeof window==='object'?window:null);
